<script setup lang="ts">
const {
  type = "button",
  label = "",
  icon = "",
  iconPosition = "left",
  theme = "main",
  variant = "default",
  size = "medium",
  disabled = false,
} = defineProps<{
  type?: "button" | "submit" | "reset";
  label?: string;
  icon?: string;
  iconPosition?: "left" | "right";
  theme?: "main" | "auth" | "overlay";
  variant?: "default" | "ghost";
  size?: "small" | "medium" | "large";
  disabled?: boolean;
}>();

const classObject = computed(() => ({
  [`is-theme-${theme}`]: theme,
  [`is-variant-${variant}`]: variant,
  [`is-size-${size}`]: size,
  [`has-icon-${iconPosition}`]: iconPosition,
  "has-large-icon": icon && !label,
}));
</script>

<template>
  <button
    class="base-button"
    :type="type"
    :class="classObject"
    :disabled="disabled"
  >
    <slot>
      <span>{{ label }}</span>
      <BaseIcon v-if="icon" :name="icon" class="icon" />
    </slot>
  </button>
</template>

<style lang="scss" scoped>
.base-button {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3rem;
  box-sizing: border-box;
  border: 2px solid var(--main-button-background-color);
  border-radius: 3em;
  background-color: var(--main-button-background-color);
  color: var(--main-button-text-color);
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.15s ease-in-out;
  padding: 0 1em;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }

  &:focus {
    border-color: var(--main-button-focus-color);
    outline: none;
  }

  &:disabled {
    opacity: 0.25;
    pointer-events: none;
  }

  &.is-variant-ghost {
    background: none;
    color: var(--main-button-background-color);
  }

  &.is-theme-auth {
    border-color: var(--auth-button-background-color);
    background-color: var(--auth-button-background-color);
    color: var(--auth-button-text-color);

    &:focus {
      border-color: var(--auth-button-focus-color);
    }

    &.is-variant-ghost {
      background: none;
      color: var(--auth-button-background-color);
    }
  }

  &.is-theme-overlay {
    border-color: var(--overlay-button-background-color);
    background-color: var(--overlay-button-background-color);
    color: var(--overlay-button-text-color);

    &:focus {
      border-color: var(--overlay-button-focus-color);
    }

    &.is-variant-ghost {
      background: none;
      color: var(--overlay-button-background-color);
    }
  }

  &.is-size-small {
    height: 2.5em;
    font-size: 0.8rem;
  }

  &.is-size-large {
    height: 3.5em;
  }

  // &.has-icon-left {
  //   .icon {
  //     order: -1;
  //   }
  // }

  // &.has-large-icon {
  //   padding: 0 0.5rem;
  //   .icon {
  //     font-size: 2.2rem;
  //   }
  // }
}
</style>
